import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import { timeEntriesApi, projectsApi, activitiesApi, usersApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { format, getISOWeek } from "date-fns";
import { sv } from "date-fns/locale";
import { Plus, Clock, CalendarIcon, Pencil, Trash2, Send, User, Star } from "lucide-react";
import { cn } from "../lib/utils";
import { getProjectColor } from "../lib/branding";

const TimeEntry = () => {
  const { user, isAdmin } = useAuth();
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [newActivityName, setNewActivityName] = useState("");
  const [showNewActivity, setShowNewActivity] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const [favorites, setFavorites] = useState([]);
  
  // Quick entry form (always visible)
  const [quickForm, setQuickForm] = useState({
    project_id: "",
    activity_id: "",
    date: searchParams.get("date") || format(new Date(), "yyyy-MM-dd"),
    hours: "",
    description: "",
    user_id: searchParams.get("user") || "",
    is_ata: false,
    mileage: "",
    transport_hours: "",
  });

  const loadData = useCallback(async () => {
    try {
      const [entriesRes, projectsRes, activitiesRes] = await Promise.all([
        timeEntriesApi.getAll(),
        projectsApi.getAll(),
        activitiesApi.getAll(),
      ]);
      setEntries(entriesRes.data);
      setProjects(projectsRes.data.filter((p) => p.is_active));
      setActivities(activitiesRes.data);
      
      // Load users if admin
      if (isAdmin) {
        const usersRes = await usersApi.getAll();
        setUsers(usersRes.data);
      }
    } catch (error) {
      toast.error("Kunde inte ladda data");
    } finally {
      setLoading(false);
    }
    // Load favorites
    projectsApi.getMyFavorites().then(r => setFavorites(r.data)).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load last used project/activity from localStorage
  useEffect(() => {
    const lastProject = localStorage.getItem("lastProjectId");
    const lastActivity = localStorage.getItem("lastActivityId");
    if (lastProject && projects.find(p => p.id === lastProject)) {
      setQuickForm(prev => ({ ...prev, project_id: lastProject }));
    }
    if (lastActivity && activities.find(a => a.id === lastActivity)) {
      setQuickForm(prev => ({ ...prev, activity_id: lastActivity }));
    }
  }, [projects, activities]);

  const handleQuickSubmit = async (e) => {
    e.preventDefault();
    if (!quickForm.project_id || !quickForm.hours) {
      toast.error("Välj projekt och ange timmar");
      return;
    }
    
    setSubmitting(true);
    try {
      let activityId = quickForm.activity_id;
      
      // Create new activity if needed
      if (quickForm.activity_id === "new" && newActivityName.trim()) {
        const newActivity = await activitiesApi.create({ name: newActivityName.trim() });
        activityId = newActivity.data.id;
        setActivities([...activities, newActivity.data]);
        setNewActivityName("");
        setShowNewActivity(false);
      }
      
      // If no activity selected, use a default one or create "Övrigt"
      if (!activityId || activityId === "new") {
        let defaultActivity = activities.find(a => a.name === "Övrigt");
        if (!defaultActivity) {
          const newAct = await activitiesApi.create({ name: "Övrigt" });
          defaultActivity = newAct.data;
          setActivities([...activities, defaultActivity]);
        }
        activityId = defaultActivity.id;
      }

      const entryData = {
        project_id: quickForm.project_id,
        activity_id: activityId,
        date: quickForm.date,
        hours: parseFloat(quickForm.hours),
        description: quickForm.description || "",
        is_ata: quickForm.is_ata,
        mileage: parseFloat(quickForm.mileage) || 0,
        transport_hours: parseFloat(quickForm.transport_hours) || 0,
      };
      
      // Admin can assign to other users
      if (isAdmin && quickForm.user_id) {
        entryData.user_id = quickForm.user_id;
      }

      await timeEntriesApi.create(entryData);
      
      // Save last used
      localStorage.setItem("lastProjectId", quickForm.project_id);
      localStorage.setItem("lastActivityId", activityId);
      
      toast.success("Tid sparad!");
      setQuickForm(prev => ({ ...prev, hours: "", description: "" }));
      loadData();
    } catch (error) {
      toast.error("Kunde inte spara");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setQuickForm({
      project_id: entry.project_id,
      activity_id: entry.activity_id,
      date: entry.date,
      hours: entry.hours.toString(),
      description: entry.description || "",
    });
    setSelectedDate(new Date(entry.date));
    setDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await timeEntriesApi.update(editingEntry.id, {
        project_id: quickForm.project_id,
        activity_id: quickForm.activity_id,
        date: quickForm.date,
        hours: parseFloat(quickForm.hours),
        description: quickForm.description || "",
      });
      toast.success("Uppdaterad!");
      setDialogOpen(false);
      setEditingEntry(null);
      loadData();
    } catch (error) {
      toast.error("Kunde inte uppdatera");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!confirm("Ta bort?")) return;
    try {
      await timeEntriesApi.delete(entryId);
      toast.success("Borttagen");
      loadData();
    } catch (error) {
      toast.error("Kunde inte ta bort");
    }
  };

  const setQuickHours = (hours) => {
    setQuickForm(prev => ({ ...prev, hours: hours.toString() }));
  };

  // Copy yesterday's entries to today
  const handleCopyYesterday = async () => {
    const yesterday = format(new Date(Date.now() - 86400000), "yyyy-MM-dd");
    const today = format(new Date(), "yyyy-MM-dd");
    // Only copy MY entries from yesterday
    const myId = user?.id;
    const yesterdayEntries = entries.filter(e => e.date === yesterday && e.user_id === myId);
    
    if (yesterdayEntries.length === 0) {
      toast.error("Inga av dina poster att kopiera från igår");
      return;
    }
    
    const todayEntries = entries.filter(e => e.date === today && e.user_id === myId);
    if (todayEntries.length > 0) {
      if (!confirm(`Du har redan ${todayEntries.length} poster idag. Vill du kopiera igårs ${yesterdayEntries.length} poster ändå?`)) return;
    }
    
    setSubmitting(true);
    try {
      let count = 0;
      for (const entry of yesterdayEntries) {
        await timeEntriesApi.create({
          project_id: entry.project_id,
          activity_id: entry.activity_id,
          date: today,
          hours: entry.hours,
          description: entry.description || "",
          mileage: entry.mileage || 0,
        });
        count++;
      }
      toast.success(`${count} poster kopierade från igår!`);
      loadData();
    } catch (error) {
      toast.error("Kunde inte kopiera");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLedig = async () => {
    setSubmitting(true);
    try {
      // Find or create "Ledig" activity
      let ledigActivity = activities.find(a => a.name.toLowerCase() === "ledig");
      if (!ledigActivity) {
        const newAct = await activitiesApi.create({ name: "Ledig" });
        ledigActivity = newAct.data;
        setActivities(prev => [...prev, ledigActivity]);
      }

      // Use first project as placeholder (required by schema)
      const projectId = quickForm.project_id || (projects.length > 0 ? projects[0].id : "");
      if (!projectId) {
        toast.error("Inget projekt finns — skapa ett projekt först");
        setSubmitting(false);
        return;
      }

      const entryData = {
        project_id: projectId,
        activity_id: ledigActivity.id,
        date: quickForm.date,
        hours: 0,
        description: "Ledig",
      };

      if (isAdmin && quickForm.user_id) {
        entryData.user_id = quickForm.user_id;
      }

      await timeEntriesApi.create(entryData);
      toast.success("Ledig rapporterad!");
      loadData();
    } catch (error) {
      toast.error("Kunde inte rapportera ledig");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(date);
      setQuickForm(prev => ({ ...prev, date: format(date, "yyyy-MM-dd") }));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      väntande: "bg-amber-500/20 text-amber-400 border-amber-500/30",
      godkänd: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
      avvisad: "bg-red-500/20 text-red-400 border-red-500/30",
    };
    return <Badge className={styles[status] || ""} variant="outline">{status}</Badge>;
  };

  // Group entries by date - memoized
  const entriesByDate = useMemo(() => entries.reduce((acc, entry) => {
    const date = entry.date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(entry);
    return acc;
  }, {}), [entries]);

  const sortedDates = useMemo(() => Object.keys(entriesByDate).sort((a, b) => b.localeCompare(a)), [entriesByDate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6dbf4a]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="time-entry-page">
      {/* Page header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Clock className="w-5 h-5 text-[#6dbf4a]" />
          <h1 className="text-2xl font-bold text-white tracking-tight">Tidsrapportering</h1>
        </div>
        <p className="text-white/25 text-sm">Rapportera och hantera dina timmar</p>
      </div>

      {/* Quick Entry Form */}
      <Card className="bg-[#0d1218] border-white/[0.06]">
        <CardContent className="pt-5 pb-5">
          <form onSubmit={handleQuickSubmit} className="space-y-4">
            {/* Admin: Select user to add time for */}
            {isAdmin && users.length > 0 && (
              <div className="flex items-center gap-3 p-3 bg-[#080c12] rounded-xl border border-[#6dbf4a]/30">
                <User className="w-5 h-5 text-[#6dbf4a]" />
                <span className="text-sm text-white/40">Lägg tid för:</span>
                <Select
                  value={quickForm.user_id || ""}
                  onValueChange={(value) => setQuickForm(prev => ({ ...prev, user_id: value === "self" ? "" : value }))}
                >
                  <SelectTrigger className="w-48 bg-[#0d1218] border-white/[0.06] text-white">
                    <SelectValue placeholder="Mig själv" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0d1218] border-white/[0.06]">
                    <SelectItem value="self" className="text-white">Mig själv</SelectItem>
                    {users.filter(u => u.id !== user?.id).map((u) => (
                      <SelectItem key={u.id} value={u.id} className="text-white">
                        {u.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Row 1: Date + Project */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal bg-[#080c12] border-white/[0.06] text-white hover:bg-[#333]"
                    data-testid="quick-date-picker"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-[#6dbf4a]" />
                    v{getISOWeek(selectedDate)} · {format(selectedDate, "EEE d MMM", { locale: sv })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#0d1218] border-white/[0.06]" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    locale={sv}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              <Select
                value={quickForm.project_id}
                onValueChange={(value) => setQuickForm(prev => ({ ...prev, project_id: value }))}
              >
                <SelectTrigger className="bg-[#080c12] border-white/[0.06] text-white" data-testid="quick-project-select">
                  <SelectValue placeholder="Valj projekt" />
                </SelectTrigger>
                <SelectContent className="bg-[#0d1218] border-white/[0.06] max-h-72">
                  {/* Favorites first */}
                  {favorites.length > 0 && projects.filter(p => favorites.includes(p.id)).length > 0 && (
                    <div>
                      <div className="px-2 py-1.5 text-[10px] font-semibold text-amber-400/60 uppercase tracking-wider flex items-center gap-1">
                        <Star className="w-3 h-3" fill="currentColor" /> Favoriter
                      </div>
                      {projects.filter(p => favorites.includes(p.id)).map(p => (
                        <SelectItem key={`fav-${p.id}`} value={p.id} className="text-white pl-4">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: getProjectColor(p.id) }} />
                            {p.name}
                          </span>
                        </SelectItem>
                      ))}
                      <div className="h-px bg-white/[0.04] my-1" />
                    </div>
                  )}
                  {/* Grouped by client */}
                  {(() => {
                    const groups = {};
                    projects.forEach(p => {
                      const client = (p.client_name || "Ovrigt").trim();
                      if (!groups[client]) groups[client] = [];
                      groups[client].push(p);
                    });
                    return Object.entries(groups).sort(([a],[b]) => a.localeCompare(b,'sv')).map(([client, projs]) => (
                      <div key={client}>
                        <div className="px-2 py-1.5 text-[10px] font-semibold text-white/30 uppercase tracking-wider">{client}</div>
                        {projs.sort((a,b) => a.name.localeCompare(b.name,'sv')).map(p => (
                          <SelectItem key={p.id} value={p.id} className="text-white pl-4">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: getProjectColor(p.id) }} />
                              {p.name}
                            </span>
                          </SelectItem>
                        ))}
                      </div>
                    ));
                  })()}
                </SelectContent>
              </Select>
            </div>

            {/* Row 2: Activity + Hours + Submit */}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr,auto,auto] gap-3 items-end">
              <div>
                <Select
                  value={quickForm.activity_id}
                  onValueChange={(value) => {
                    setShowNewActivity(value === "new");
                    setQuickForm(prev => ({ ...prev, activity_id: value }));
                  }}
                >
                  <SelectTrigger className="bg-[#080c12] border-white/[0.06] text-white" data-testid="quick-activity-select">
                    <SelectValue placeholder="Aktivitet (valfritt)" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0d1218] border-white/[0.06]">
                    {activities.map((activity) => (
                      <SelectItem key={activity.id} value={activity.id} className="text-white">
                        {activity.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="new" className="text-[#6dbf4a] font-medium">
                      + Ny aktivitet
                    </SelectItem>
                  </SelectContent>
                </Select>
                
                {showNewActivity && (
                  <Input
                    value={newActivityName}
                    onChange={(e) => setNewActivityName(e.target.value)}
                    placeholder="Namn på ny aktivitet..."
                    className="mt-2 bg-[#080c12] border-[#6dbf4a] text-white"
                    autoFocus
                  />
                )}
              </div>

              {/* Quick hour buttons */}
              <div className="flex gap-1">
                {[1, 2, 4, 8].map((h) => (
                  <Button
                    key={h}
                    type="button"
                    variant={quickForm.hours === h.toString() ? "default" : "outline"}
                    size="sm"
                    onClick={() => setQuickHours(h)}
                    className={cn(
                      "w-10 h-10",
                      quickForm.hours === h.toString() 
                        ? "bg-[#6dbf4a] text-white hover:bg-[#5da83d]" 
                        : "bg-[#080c12] border-white/[0.06] text-white hover:bg-[#333]"
                    )}
                  >
                    {h}h
                  </Button>
                ))}
                <Input
                  type="number"
                  min="0.25"
                  step="0.25"
                  value={quickForm.hours}
                  onChange={(e) => setQuickForm(prev => ({ ...prev, hours: e.target.value }))}
                  placeholder="Tim"
                  className="w-16 h-10 bg-[#080c12] border-white/[0.06] text-white text-center"
                  data-testid="quick-hours-input"
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  onClick={() => setQuickForm(prev => ({ ...prev, is_ata: !prev.is_ata }))}
                  variant="outline"
                  className={`h-10 px-3 border-white/[0.06] ${
                    quickForm.is_ata
                      ? "bg-orange-500/20 text-orange-400 border-orange-500/40"
                      : "text-white/40 hover:bg-orange-500/20 hover:text-orange-400 hover:border-orange-500/40"
                  }`}
                  data-testid="ata-toggle-btn"
                >
                  ÄTA
                </Button>
                <Button
                  type="button"
                  onClick={handleCopyYesterday}
                  disabled={submitting}
                  variant="outline"
                  className="h-10 px-3 border-white/[0.06] text-white/40 hover:bg-blue-500/20 hover:text-blue-400 hover:border-blue-500/40"
                  data-testid="copy-yesterday-btn"
                >
                  Kopiera igår
                </Button>
                <Button
                  type="button"
                  onClick={handleLedig}
                  disabled={submitting}
                  variant="outline"
                  className="h-10 px-4 border-white/[0.06] text-white/40 hover:bg-amber-500/20 hover:text-amber-400 hover:border-amber-500/40"
                  data-testid="ledig-btn"
                >
                  Ledig
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || !quickForm.project_id || !quickForm.hours}
                  className="h-10 px-6 flex-1 sm:flex-none bg-[#6dbf4a] hover:bg-[#5da83d] text-white"
                  data-testid="quick-submit-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Spara
                </Button>
              </div>
            </div>

            {/* Optional description + mileage + transport */}
            <div className="flex gap-2">
              <Input
                value={quickForm.description}
                onChange={(e) => setQuickForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Anteckning (valfritt)"
                className="flex-1 bg-[#080c12] border-white/[0.06] text-white placeholder:text-white/40"
                data-testid="quick-description-input"
              />
              <Input
                type="number"
                min="0"
                step="1"
                value={quickForm.transport_hours}
                onChange={(e) => setQuickForm(prev => ({ ...prev, transport_hours: e.target.value.replace(/[^0-9]/g, '') }))}
                placeholder="Transport (h)"
                className="w-28 bg-[#080c12] border-white/[0.06] text-white placeholder:text-white/40"
                data-testid="quick-transport-input"
              />
              <Input
                type="number"
                min="0"
                step="0.1"
                value={quickForm.mileage}
                onChange={(e) => setQuickForm(prev => ({ ...prev, mileage: e.target.value }))}
                placeholder="Mil"
                className="w-20 bg-[#080c12] border-white/[0.06] text-white placeholder:text-white/40"
                data-testid="quick-mileage-input"
              />
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Time Entries List */}
      {sortedDates.length > 0 ? (
        <div className="space-y-4">
          {sortedDates.map((date) => {
            const dayTotal = entriesByDate[date].reduce((sum, e) => sum + e.hours, 0);
            return (
              <div key={date}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-white/40">
                    {format(new Date(date), "EEEE d MMMM", { locale: sv })}
                  </h3>
                  <span className="text-lg font-bold text-[#6dbf4a] bg-[#6dbf4a]/10 px-3 py-1 rounded-full">{dayTotal}h</span>
                </div>
                <div className="space-y-1.5">
                  {entriesByDate[date].map((entry) => (
                    <div
                      key={entry.id}
                      className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-[#0d1218] border border-white/[0.04] hover:border-white/[0.08] transition-colors"
                      data-testid={`time-entry-${entry.id}`}
                    >
                      <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-white text-sm">{entry.project_name}</span>
                        <span className="text-white/15">·</span>
                        <span className="text-xs text-white/35">{entry.activity_name}</span>
                        {getStatusBadge(entry.status)}
                        {entry.is_ata && (
                          <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-[10px] py-0" variant="outline">ÄTA</Badge>
                        )}
                        {entry.invoiced && (
                          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px] py-0" variant="outline">Fakturerad</Badge>
                        )}
                        {isAdmin && entry.user_name && (
                          <span className="text-xs text-white/25 flex items-center gap-1"><User className="w-3 h-3" />{entry.user_name}</span>
                        )}
                        {entry.description && (
                          <span className="text-xs text-white/20 truncate max-w-[200px]">{entry.description}</span>
                        )}
                        {entry.mileage > 0 && (
                          <span className="text-xs text-blue-400/60">{entry.mileage} mil</span>
                        )}
                        {entry.transport_hours > 0 && (
                          <span className="text-xs text-purple-400/60">{entry.transport_hours}h transport</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="stat-number text-lg text-white">{entry.hours}<span className="text-sm text-white/30 ml-0.5">h</span></span>
                        {(entry.status === "väntande" || isAdmin) && (
                          <div className="flex gap-0.5">
                            <Button variant="ghost" size="icon" onClick={() => handleEdit(entry)} className="h-7 w-7 text-white/25 hover:text-white">
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(entry.id)} className="h-7 w-7 text-white/25 hover:text-red-400">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Card className="bg-[#0d1218] border-white/[0.06]">
          <CardContent className="py-12 text-center">
            <Clock className="w-12 h-12 mx-auto mb-4 text-white/25" />
            <p className="text-white/40">Inga tidsposter ännu</p>
            <p className="text-sm text-white/40">Använd formuläret ovan för att lägga till tid</p>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0d1218] border-white/[0.06]">
          <DialogHeader>
            <DialogTitle className="text-white">Redigera tidspost</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Select
                value={quickForm.project_id}
                onValueChange={(value) => setQuickForm(prev => ({ ...prev, project_id: value }))}
              >
                <SelectTrigger className="bg-[#080c12] border-white/[0.06] text-white">
                  <SelectValue placeholder="Projekt" />
                </SelectTrigger>
                <SelectContent className="bg-[#0d1218] border-white/[0.06]">
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id} className="text-white">
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={quickForm.activity_id}
                onValueChange={(value) => setQuickForm(prev => ({ ...prev, activity_id: value }))}
              >
                <SelectTrigger className="bg-[#080c12] border-white/[0.06] text-white">
                  <SelectValue placeholder="Aktivitet" />
                </SelectTrigger>
                <SelectContent className="bg-[#0d1218] border-white/[0.06]">
                  {activities.map((activity) => (
                    <SelectItem key={activity.id} value={activity.id} className="text-white">
                      {activity.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2">
              {[1, 2, 4, 8].map((h) => (
                <Button
                  key={h}
                  type="button"
                  variant={quickForm.hours === h.toString() ? "default" : "outline"}
                  size="sm"
                  onClick={() => setQuickForm(prev => ({ ...prev, hours: h.toString() }))}
                  className={cn(
                    "flex-1",
                    quickForm.hours === h.toString() 
                      ? "bg-[#6dbf4a] text-white" 
                      : "bg-[#080c12] border-white/[0.06] text-white"
                  )}
                >
                  {h}h
                </Button>
              ))}
              <Input
                type="number"
                min="0.25"
                step="0.25"
                value={quickForm.hours}
                onChange={(e) => setQuickForm(prev => ({ ...prev, hours: e.target.value }))}
                className="w-20 bg-[#080c12] border-white/[0.06] text-white text-center"
              />
            </div>

            <Input
              value={quickForm.description}
              onChange={(e) => setQuickForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Anteckning"
              className="bg-[#080c12] border-white/[0.06] text-white"
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-white/[0.06] text-white/40">
                Avbryt
              </Button>
              <Button type="submit" disabled={submitting} className="bg-[#6dbf4a] hover:bg-[#5da83d]">
                Spara
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeEntry;
